import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeftIcon } from 'lucide-react';
import { useGateValue } from '@statsig/react-bindings';
import { useUserDetails } from '@/hooks/useUserDetails';
import { useBootup } from '@/hooks/useBootup';
import { useAiRecommendationData } from '@viome/viome-base/src/usecase/AiRecommendation/AiRecommendationQuery';
import { isWrappedEligibleV2 } from '@/utils/isWrappedEligible';
import { HOME_PATH } from '@/constants/paths';
import EventTracker from '@/utils/EventTracker';

const WRAPPED_ORIGIN =
  (import.meta.env.VITE_WRAPPED_ORIGIN as string | undefined) ??
  'https://recipes.viome.com';
const WRAPPED_IFRAME_URL = `${WRAPPED_ORIGIN}/wrapped/v2?embed=true`;
const READY_TIMEOUT_MS = 10_000;

type WrappedReady = { type: 'WRAPPED_READY'; version: 1 };
type NavigateBack = { type: 'VIOME_NAVIGATE_BACK'; source?: string };
type WrappedTelemetry = {
  type: 'WRAPPED_TELEMETRY';
  event: string;
  properties?: Record<string, unknown>;
};

type IncomingMessage = WrappedReady | NavigateBack | WrappedTelemetry;

const FORWARDED_TELEMETRY_EVENTS = new Set([
  'wrapped_completed',
  'wrapped_dismissed',
  'wrapped_generation_failed',
]);

export const WrappedPageV2: React.FC = () => {
  const navigate = useNavigate();
  const iframeRef = useRef<HTMLIFrameElement>(null);

  const userDetails = useUserDetails();
  const { firstName } = userDetails;
  // user_phi_id is the short numeric PHI user id that the new AI rec
  // endpoint expects in the path. It's present on the User payload at
  // runtime but isn't in viome-base's User TypeScript model yet — cast
  // through unknown until we extend the type in viome-base.
  const userPhiId = (userDetails as unknown as { user_phi_id?: number })
    ?.user_phi_id;
  const { bootupState } = useBootup();

  const aiRecQuery = useAiRecommendationData(userPhiId);

  const [readyAcknowledged, setReadyAcknowledged] = useState(false);
  const [didTimeout, setDidTimeout] = useState(false);
  const [didSendPayload, setDidSendPayload] = useState(false);

  const wrappedFlagEnabled = useGateValue('enable-viome-wrapped');
  const aiData = aiRecQuery.data;
  const eligible = isWrappedEligibleV2(aiData);
  // Kit details bootstrap is separate from the AI rec call. Top-level kitId
  // is convenience only — aiRecommendation.user_profile.kit_id is authoritative.
  const kitId = bootupState?.kitDetailsData?.kitId ?? undefined;

  const isLoading = aiRecQuery.isLoading;
  // Treat a missing user_phi_id as a not-eligible state instead of an
  // indefinite loading state. The AI rec hook is disabled when the id is
  // missing, so without this we'd hang forever on "Preparing your Digest…".
  // Removed once the user_phi_id source is wired up upstream.
  const isError = aiRecQuery.isError || userPhiId === undefined;

  const goBack = useCallback(() => {
    navigate(HOME_PATH);
  }, [navigate]);

  const sendPayload = useCallback(() => {
    const target = iframeRef.current?.contentWindow;
    if (!target || !aiData) return;

    target.postMessage(
      {
        type: 'WRAPPED_PAYLOAD',
        version: 2,
        payload: {
          aiRecommendation: aiData,
          firstName: firstName ?? '',
          ...(kitId ? { kitId } : {}),
        },
      },
      WRAPPED_ORIGIN,
    );
    setDidSendPayload(true);
  }, [aiData, firstName, kitId]);

  useEffect(() => {
    const handleMessage = (event: MessageEvent<IncomingMessage>) => {
      if (event.origin !== WRAPPED_ORIGIN) return;
      if (event.source !== iframeRef.current?.contentWindow) return;

      const data = event.data;
      if (!data?.type) return;

      if (data.type === 'WRAPPED_READY') {
        setReadyAcknowledged(true);
        EventTracker.trackEvent('wrapped', 'wrapped_started');
        return;
      }

      if (data.type === 'VIOME_NAVIGATE_BACK' && data.source === 'wrapped') {
        goBack();
        return;
      }

      if (
        data.type === 'WRAPPED_TELEMETRY' &&
        FORWARDED_TELEMETRY_EVENTS.has(data.event)
      ) {
        EventTracker.trackEvent('wrapped', data.event);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [goBack]);

  useEffect(() => {
    if (didSendPayload) return;
    if (!readyAcknowledged) return;
    if (!aiData) return;
    sendPayload();
  }, [didSendPayload, readyAcknowledged, aiData, sendPayload]);

  useEffect(() => {
    if (readyAcknowledged) return;
    const id = window.setTimeout(() => {
      setDidTimeout(true);
      EventTracker.trackEvent('wrapped', 'wrapped_handshake_timeout');
    }, READY_TIMEOUT_MS);
    return () => window.clearTimeout(id);
  }, [readyAcknowledged]);

  const ineligibleLoggedRef = useRef(false);
  const showIneligible =
    !isLoading && (!wrappedFlagEnabled || isError || !aiData || !eligible);
  useEffect(() => {
    if (!showIneligible || ineligibleLoggedRef.current) return;
    ineligibleLoggedRef.current = true;
    EventTracker.trackEvent('wrapped', 'wrapped_ineligible');
  }, [showIneligible]);

  const renderHeader = () => (
    <div className="bg-white px-4 py-3">
      <button
        onClick={goBack}
        className="flex items-center text-primary hover:opacity-70 transition-opacity p-3 md:p-2"
        aria-label="Go back"
      >
        <ChevronLeftIcon size={24} />
      </button>
    </div>
  );

  if (isLoading) {
    return (
      <div className="w-full flex-1 flex flex-col min-h-0">
        {renderHeader()}
        <div className="p-4 text-center flex-grow flex items-center justify-center">
          Preparing your Digest…
        </div>
      </div>
    );
  }

  if (showIneligible) {
    return (
      <div className="w-full flex-1 flex flex-col min-h-0">
        {renderHeader()}
        <div className="p-4 text-center flex-grow flex items-center justify-center">
          Your Digest isn&apos;t available for your account yet.
        </div>
      </div>
    );
  }

  if (didTimeout && !readyAcknowledged) {
    return (
      <div className="w-full flex-1 flex flex-col min-h-0">
        {renderHeader()}
        <div className="p-4 text-center flex-grow flex items-center justify-center">
          Couldn&apos;t open your Digest. Please try again later.
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full flex flex-col">
      {renderHeader()}
      <iframe
        ref={iframeRef}
        src={WRAPPED_IFRAME_URL}
        title="Viome Digest"
        className="w-full border-none flex-grow min-h-0"
        allow="clipboard-write"
      />
    </div>
  );
};

export default WrappedPageV2;
